import { useState, type FormEvent } from "react";
import { useRouterState } from "@tanstack/react-router";
import { submitContact } from "@/lib/contact.functions";
import { detectLocale, pathMap } from "@/lib/locale";
import { trackConversion } from "@/lib/analytics";

type Locale = "de" | "en";

// Maps URL-friendly ?topic= slugs to the full topic labels used in the select.
const TOPIC_SLUG_MAP: Record<Locale, Record<string, string>> = {
  de: {
    "performance-marketing": "Performance Marketing",
    pm: "Performance Marketing",
    seo: "SEO & AI SEO",
    "ai-seo": "SEO & AI SEO",
    cro: "Conversion-Optimierung",
    "conversion-optimierung": "Conversion-Optimierung",
    tracking: "Tracking & Datenbasis",
    strategie: "Strategie & Sparring",
    website: "Website / Shop / Software",
    tools: "Tool-Demo (Delta / picazzoo / GonzalAds)",
    "region-wuerzburg": "Performance Marketing",
    "region-frankfurt": "Performance Marketing",
    "region-aschaffenburg": "Performance Marketing",
    "region-schweinfurt": "Performance Marketing",
    "region-nuernberg": "Performance Marketing",
  },
  en: {
    "performance-marketing": "Performance Marketing",
    pm: "Performance Marketing",
    seo: "SEO & AI SEO",
    "ai-seo": "SEO & AI SEO",
    cro: "Conversion Optimization",
    "conversion-optimization": "Conversion Optimization",
    tracking: "Tracking & Data Foundation",
    strategy: "Strategic Sparring",
    website: "Website / Shop / Software",
    tools: "Tool Demo (Delta / picazzoo / GonzalAds)",
    "region-wuerzburg": "Performance Marketing",
    "region-frankfurt": "Performance Marketing",
    "region-aschaffenburg": "Performance Marketing",
    "region-schweinfurt": "Performance Marketing",
    "region-nuernberg": "Performance Marketing",
  },
};

const COPY = {
  de: {
    topics: [
      "Performance Marketing",
      "SEO & AI SEO",
      "Conversion-Optimierung",
      "Tracking & Datenbasis",
      "Strategie & Sparring",
      "Website / Shop / Software",
      "Tool-Demo (Delta / picazzoo / GonzalAds)",
      "Sonstiges",
    ],
    labels: {
      company: "Unternehmen",
      name: "Name",
      email: "E-Mail",
      topic: "Worum geht es?",
      message: "Kurze Beschreibung",
    },
    placeholders: {
      company: "Firma oder Marke",
      name: "Vor- & Nachname",
      email: "ihr@unternehmen.de",
      topicSelect: "Auswählen …",
      message: "Mediabudget, aktuelle Situation, Ziel — gern in zwei bis drei Sätzen.",
      strategyMessage:
        "Aktuelles Geschäftsmodell:\n[Bitte kurz beschreiben]\n\nAktuelle Performance-Marketing-Situation:\n[Was läuft, was nicht]\n\nWas Sie sich vom Strategiegespräch erhoffen:\n[Konkrete Fragen oder Themen]",
    },
    strategyTopic: "Strategie & Sparring",
    strategyBanner: {
      eyebrow: "Strategiegespräch · kostenfrei",
      body: "Sie haben das kostenfreie Strategiegespräch angefragt. 30 Minuten, telefonisch oder per Videocall. Beschreiben Sie unten kurz Ihre aktuelle Situation — wir melden uns innerhalb von einem Werktag mit Terminvorschlägen.",
    },
    strategyFootnote:
      "Methodisches Erstgespräch ohne Sales-Pitch. Wir bereiten uns auf Basis Ihrer Beschreibung konkret vor.",
    submit: "Nachricht senden →",
    sending: "Sende …",
    successEyebrow: "Nachricht eingegangen",
    successHead: "Danke — wir melden uns.",
    successBody: (
      <>
        Innerhalb von 48 Stunden, werktags. Wenn die Sache eilt, schreiben Sie uns parallel an{" "}
        <a className="text-cyan-deep underline" href="mailto:hello@calvarius.com">
          hello@calvarius.com
        </a>
        .
      </>
    ),
    unknownError: "Unbekannter Fehler",
    disclaimerPre: "Mit dem Absenden stimmen Sie unserer ",
    disclaimerLink: "Datenschutzerklärung",
    disclaimerPost: " zu. Wir antworten in der Regel binnen 48 Stunden, werktags.",
  },
  en: {
    topics: [
      "Performance Marketing",
      "SEO & AI SEO",
      "Conversion Optimization",
      "Tracking & Data Foundation",
      "Strategic Sparring",
      "Website / Shop / Software",
      "Tool Demo (Delta / picazzoo / GonzalAds)",
      "Other",
    ],
    labels: {
      company: "Company",
      name: "Name",
      email: "Email",
      topic: "What is it about?",
      message: "Brief description",
    },
    placeholders: {
      company: "Company or brand",
      name: "First & last name",
      email: "you@company.com",
      topicSelect: "Select …",
      message: "Media budget, current situation, goal — in two or three sentences.",
      strategyMessage:
        "Current business model:\n[Please briefly describe]\n\nCurrent performance marketing situation:\n[What works, what doesn't]\n\nWhat you hope to gain from the strategy meeting:\n[Specific questions or topics]",
    },
    strategyTopic: "Strategic Sparring",
    strategyBanner: {
      eyebrow: "Strategy meeting · free",
      body: "You have requested the free strategy meeting. 30 minutes, by phone or video call. Briefly describe your current situation below — we will get back to you within one business day with appointment suggestions.",
    },
    strategyFootnote:
      "Methodical initial meeting without sales pitch. We prepare concretely based on your description.",
    submit: "Send message →",
    sending: "Sending …",
    successEyebrow: "Message received",
    successHead: "Thanks — we'll be in touch.",
    successBody: (
      <>
        Within 48 hours on business days. If it's urgent, write us in parallel at{" "}
        <a className="text-cyan-deep underline" href="mailto:hello@calvarius.com">
          hello@calvarius.com
        </a>
        .
      </>
    ),
    unknownError: "Unknown error",
    disclaimerPre: "By submitting, you agree to our ",
    disclaimerLink: "Privacy Policy",
    disclaimerPost: ". We typically reply within 48 hours on business days.",
  },
} as const;

export function ContactForm({ locale: localeProp }: { locale?: Locale } = {}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const locale: Locale = localeProp ?? detectLocale(path);
  const c = COPY[locale];
  const privacyHref = locale === "en" ? pathMap.privacy.en : pathMap.privacy.de;

  const topicSlug = typeof search?.topic === "string" ? search.topic.toLowerCase() : "";
  const presetTopicFromSlug = TOPIC_SLUG_MAP[locale][topicSlug] ?? "";

  // Strategy meeting / viability calculator prefill
  const str = (k: string) => (typeof search?.[k] === "string" ? (search[k] as string) : "");
  const subjectSlug = (str("betreff") || str("subject")).toLowerCase();
  const isStrategyMode =
    subjectSlug === "strategiegespraech" || subjectSlug === "strategy-meeting";
  const vAov = str("aov");
  const vGm = str("grossMargin");
  const vMm = str("marketingMargin");
  const vCvr = str("conversionRate");
  const vIndustry = str("industry");
  const vCpc = str("maxCPC");
  const vCpa = str("maxCPA");
  const vVerdict = str("verdict");
  const hasViabilityData = isStrategyMode && !!vAov;
  const presetMessage = hasViabilityData
    ? locale === "de"
      ? `Anfrage Strategiegespräch — aus Wirtschaftlichkeits-Rechner.\n\nMeine Wirtschaftlichkeits-Parameter:\n· AOV: ${vAov} €\n· Bruttomarge: ${vGm}%\n· Marketing-Marge: ${vMm}%\n· Conversion-Rate: ${vCvr}%\n· Branche: ${vIndustry}\n\nErgebnis:\n· Max CPC: ${vCpc} €\n· Max CPA: ${vCpa} €\n· Verdict: ${vVerdict?.toUpperCase()}\n\nKurz zu meinem Anliegen: `
      : `Strategy meeting request — from profitability calculator.\n\nMy viability parameters:\n· AOV: €${vAov}\n· Gross margin: ${vGm}%\n· Marketing margin: ${vMm}%\n· Conversion rate: ${vCvr}%\n· Industry: ${vIndustry}\n\nResult:\n· Max CPC: €${vCpc}\n· Max CPA: €${vCpa}\n· Verdict: ${vVerdict?.toUpperCase()}\n\nBrief context on my situation: `
    : "";
  const presetTopic =
    presetTopicFromSlug || (isStrategyMode ? c.strategyTopic : "");
  const messagePlaceholder =
    isStrategyMode && !hasViabilityData
      ? c.placeholders.strategyMessage
      : c.placeholders.message;

  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setState("sending");
    setError(null);
    const fd = new FormData(form);
    try {
      await submitContact({
        data: {
          company: (fd.get("company") as string) || null,
          name: fd.get("name") as string,
          email: fd.get("email") as string,
          topic: (fd.get("topic") as string) || null,
          message: fd.get("message") as string,
        },
      });
      trackConversion("contact_form_submitted", {
        topic: (fd.get("topic") as string) || null,
        has_company: !!fd.get("company"),
        locale,
        is_strategy_meeting: isStrategyMode,
      });
      setState("ok");
      form.reset();
    } catch (err) {
      setState("error");
      let msg = err instanceof Error ? err.message : c.unknownError;
      try {
        const parsed = JSON.parse(msg);
        if (Array.isArray(parsed)) {
          msg = parsed.map((p: { message?: string }) => p.message).filter(Boolean).join(" · ") || c.unknownError;
        }
      } catch {
        // not JSON, leave as-is
      }
      setError(msg);
    }
  };

  if (state === "ok") {
    return (
      <div className="rounded-lg border border-lime/40 bg-lime-tint p-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-lime-deep">
          {c.successEyebrow}
        </div>
        <h3 className="mt-3 font-display text-2xl text-ink">{c.successHead}</h3>
        <p className="mt-3 text-ink-3">{c.successBody}</p>
      </div>
    );
  }

  const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3";
  const inputCls =
    "w-full rounded-md border border-rule bg-white px-4 py-3 text-ink outline-none transition focus:border-cyan-deep focus:ring-2 focus:ring-cyan-deep/20";

  return (
    <form onSubmit={onSubmit} className="rounded-lg border border-rule bg-white p-7 md:p-9">
      {isStrategyMode && (
        <div className="mb-7 rounded-md border border-lime/50 bg-lime/15 p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-lime-deep">
            {c.strategyBanner.eyebrow}
          </div>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-2">
            {c.strategyBanner.body}
          </p>
        </div>
      )}

      <label className={labelCls} htmlFor="company">{c.labels.company}</label>
      <input className={inputCls} id="company" name="company" type="text" placeholder={c.placeholders.company} />

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="name">{c.labels.name}</label>
          <input className={inputCls} id="name" name="name" type="text" required placeholder={c.placeholders.name} />
        </div>
        <div>
          <label className={labelCls} htmlFor="email">{c.labels.email}</label>
          <input className={inputCls} id="email" name="email" type="email" required placeholder={c.placeholders.email} />
        </div>
      </div>

      <label className={`${labelCls} mt-5`} htmlFor="topic">{c.labels.topic}</label>
      <select key={presetTopic} className={inputCls} id="topic" name="topic" defaultValue={presetTopic}>
        <option value="">{c.placeholders.topicSelect}</option>
        {c.topics.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      {hasViabilityData && (
        <div className="mt-5 rounded-md border border-cyan/30 bg-bg-subtle px-4 py-3 text-sm text-ink-2">
          {locale === "de"
            ? "Ihre Wirtschaftlichkeits-Daten wurden übernommen — Sie können diese im Nachrichten-Feld noch anpassen."
            : "Your viability data has been pre-filled — you can still adjust it in the message field."}
        </div>
      )}

      <label className={`${labelCls} mt-5`} htmlFor="message">{c.labels.message}</label>
      <textarea
        key={presetMessage ? "prefill" : isStrategyMode ? "strategy" : "blank"}
        className={`${inputCls} min-h-[140px] resize-y`}
        id="message"
        name="message"
        required
        defaultValue={presetMessage}
        placeholder={messagePlaceholder}
      />

      <button
        type="submit"
        disabled={state === "sending"}
        className="mt-6 inline-flex items-center justify-center rounded-md bg-lime px-6 py-3 font-semibold text-ink transition-colors hover:bg-lime-hover disabled:opacity-60"
      >
        {state === "sending" ? c.sending : c.submit}
      </button>

      {isStrategyMode && (
        <p className="mt-4 text-sm leading-relaxed text-ink-3">
          {c.strategyFootnote}
        </p>
      )}

      {error && (
        <p className="mt-4 text-sm text-destructive">{error}</p>
      )}

      <p className="mt-5 font-mono text-[11px] leading-relaxed text-ink-4">
        {c.disclaimerPre}
        <a className="underline hover:text-ink-3" href={privacyHref}>
          {c.disclaimerLink}
        </a>
        {c.disclaimerPost}
      </p>
    </form>
  );
}
